// FILE: components/wallet/TopupQrModal.tsx
// LÀM GÌ? Popup nạp kim cương ngay tại chỗ: tạo đơn PayOS → hiện VietQR + STK → poll 3s → PAID thì đồng bộ số dư và gọi onPaid.
// VÌ SAO TỒN TẠI? Không bắt user rời trang đang xem (chuẩn ngành), nạp xong mua luôn.
// DB SCHEMA LIÊN QUAN? payment_orders (pending → completed qua RPC complete_payment_order), users.diamonds
// FILE NÀO DÙNG NÓ? components/shared/PurchaseConfirmModal.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Gem, Loader2, X, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { useDiamonds } from "@/lib/DiamondsContext";
import { verifyTopup, syncBalance } from "@/lib/wallet-client";

const MIN_TOPUP = 10;
const POLL_MS = 3000;
const EXPIRE_MS = 15 * 60_000;

interface Order {
  orderCode: number;
  qrCode: string;
  checkoutUrl: string;
  accountNumber?: string;
  accountName?: string;
  description?: string;
  amountVnd: number;
}

export default function TopupQrModal({
  need,
  onPaid,
  onClose,
}: {
  need: number;            // số 💎 còn thiếu
  onPaid: () => void;      // gọi sau khi tiền đã vào ví
  onClose: () => void;
}) {
  const base = Math.max(MIN_TOPUP, Math.ceil(need));
  const options = Array.from(new Set([base, Math.max(100, base), Math.max(200, base * 2)])).sort((a, b) => a - b);

  const [amount, setAmount] = useState(base);
  const [order, setOrder] = useState<Order | null>(null);
  const [status, setStatus] = useState<"creating" | "waiting" | "paid" | "error">("creating");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const { diamonds, addDiamonds } = useDiamonds();
  const balRef = useRef(diamonds);
  balRef.current = diamonds;

  async function createOrder(amt: number) {
    setAmount(amt); setStatus("creating"); setError(""); setOrder(null);
    try {
      const res = await fetch("/api/wallet/topup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt, returnPath: window.location.pathname }),
      });
      const d = await res.json();
      if (!res.ok || !d.qrCode) throw new Error(d.error ?? "Không tạo được mã QR");
      setOrder(d);
      setStatus("waiting");
    } catch (e: any) {
      setError(e?.message ?? "Lỗi kết nối, thử lại sau");
      setStatus("error");
    }
  }

  useEffect(() => { createOrder(base); /* eslint-disable-next-line */ }, []);

  // Poll trạng thái đơn
  useEffect(() => {
    if (status !== "waiting" || !order) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const started = Date.now();
    const tick = async () => {
      if (stop) return;
      const s = await verifyTopup(order.orderCode);
      if (stop) return;
      if (s === "paid") {
        setStatus("paid");
        await syncBalance(balRef.current, addDiamonds);
        setTimeout(onPaid, 900);
        return;
      }
      if (s === "cancelled") { setError("Đơn đã huỷ hoặc hết hạn. Hãy tạo mã mới."); setStatus("error"); return; }
      if (Date.now() - started > EXPIRE_MS) { setError("Mã QR đã hết hạn. Hãy tạo mã mới."); setStatus("error"); return; }
      timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, POLL_MS);
    return () => { stop = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, order]);

  const copy = (key: string, val?: string) => {
    if (!val) return;
    navigator.clipboard?.writeText(val).then(() => { setCopied(key); setTimeout(() => setCopied(""), 1500); });
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      onClick={e => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Nạp kim cương"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={status === "paid" ? undefined : onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3 border-b border-border">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Gem className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-[15px] font-extrabold text-foreground">Nạp kim cương</h3>
            <p className="text-[11px] text-muted-foreground">Bạn còn thiếu {Math.ceil(need)} 💎 · quét QR bằng app ngân hàng</p>
          </div>
          {status !== "paid" && (
            <button onClick={onClose} aria-label="Đóng" className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="px-5 py-4">
          {/* Chọn gói */}
          {status !== "paid" && (
            <div className="grid grid-cols-3 gap-2 mb-4">
              {options.map(o => (
                <button key={o} onClick={() => o !== amount && createOrder(o)} disabled={status === "creating"}
                  className={`py-2 rounded-xl border-2 text-center transition-all ${amount === o ? "border-orange-500 bg-orange-50" : "border-border hover:border-orange-300"}`}>
                  <div className="text-[14px] font-extrabold text-teal-800">💎 {o}</div>
                  <div className="text-[10px] text-muted-foreground">{(o * 1000).toLocaleString("vi-VN")}đ</div>
                </button>
              ))}
            </div>
          )}

          {/* QR / trạng thái */}
          <div className="flex flex-col items-center">
            {status === "creating" && (
              <div className="w-[200px] h-[200px] rounded-xl bg-muted flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-teal-700" />
              </div>
            )}
            {status === "waiting" && order && (
              <>
                <div className="p-3 rounded-xl border border-border bg-white">
                  <QRCodeSVG value={order.qrCode} size={200} level="M" />
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-teal-700">
                  <Loader2 className="w-3 h-3 animate-spin" />Đang chờ thanh toán…
                </div>
              </>
            )}
            {status === "paid" && (
              <div className="py-8 flex flex-col items-center gap-2">
                <CheckCircle2 className="w-14 h-14 text-emerald-500" />
                <p className="text-[14px] font-extrabold text-emerald-700">Đã nhận {amount} 💎</p>
                <p className="text-[11px] text-muted-foreground">Đang mở khóa cho bạn…</p>
              </div>
            )}
            {status === "error" && (
              <div className="w-full bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                <p className="text-[12px] text-red-600 font-semibold mb-2">{error}</p>
                <button onClick={() => createOrder(amount)} className="px-4 py-1.5 rounded-full bg-orange-500 text-white text-[12px] font-bold">Tạo mã mới</button>
              </div>
            )}
          </div>

          {/* Thông tin chuyển khoản thủ công */}
          {status === "waiting" && order && (
            <div className="mt-4 rounded-xl bg-[#f8fafc] border border-border divide-y divide-border text-[12px]">
              {[
                { k: "stk", label: "Số tài khoản", val: order.accountNumber },
                { k: "name", label: "Chủ tài khoản", val: order.accountName },
                { k: "amt", label: "Số tiền", val: order.amountVnd?.toLocaleString("vi-VN") + "đ", raw: String(order.amountVnd) },
                { k: "desc", label: "Nội dung", val: order.description },
              ].filter(r => r.val).map(r => (
                <div key={r.k} className="flex items-center justify-between px-3 py-2 gap-2">
                  <span className="text-muted-foreground">{r.label}</span>
                  <button onClick={() => copy(r.k, r.raw ?? r.val)} className="flex items-center gap-1 font-bold text-foreground hover:text-teal-700 truncate">
                    <span className="truncate">{r.val}</span>
                    {copied === r.k ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <Copy className="w-3.5 h-3.5 shrink-0 opacity-50" />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {status === "waiting" && order?.checkoutUrl && (
            <a href={order.checkoutUrl} className="mt-3 flex items-center justify-center gap-1 text-[11px] text-muted-foreground hover:text-teal-700">
              <ExternalLink className="w-3 h-3" />Mở trang thanh toán PayOS
            </a>
          )}
        </div>
      </div>
    </div>
  );
}